// Static content: substances, milestones, toolkit, crisis resources

export const SUBSTANCES = {
  cigarettes: {
    key: "cigarettes",
    label: "Cigarettes",
    icon: "Cigarette",
    unit: "cigarettes",
    unitShort: "cigs/day",
    defaultBaseline: 20,
    defaultCost: 0.65,
    costLabel: "Cost per cigarette",
    medical: false,
    blurb: "Nicotine withdrawal is uncomfortable but not usually dangerous. Cravings peak in the first 72 hours, then ease.",
  },
  vaping: {
    key: "vaping",
    label: "Vaping",
    icon: "Cloud",
    unit: "puffs",
    unitShort: "puffs/day",
    defaultBaseline: 200,
    defaultCost: 0.02,
    costLabel: "Cost per puff",
    medical: false,
    blurb: "Vaping withdrawal is uncomfortable but not usually dangerous. Cutting down works best alongside nicotine replacement and support — cravings ease within days.",
  },
  chew: {
    key: "chew",
    label: "Chewing tobacco",
    icon: "Package",
    unit: "pouches",
    unitShort: "pouches/day",
    defaultBaseline: 6,
    defaultCost: 0.6,
    costLabel: "Cost per pouch",
    medical: false,
    blurb: "Smokeless-tobacco withdrawal is uncomfortable but not usually dangerous. Cravings peak in the first days, then ease — nicotine replacement and support help.",
  },
};

// Health milestones by hours-since-quit
export const MILESTONES = {
  cigarettes: [
    { hours: 0.33, title: "20 minutes", desc: "Heart rate and blood pressure begin to drop back toward normal." },
    { hours: 12, title: "12 hours", desc: "Carbon monoxide in your blood falls to normal levels." },
    { hours: 48, title: "2 days", desc: "Nerve endings start to regrow. Smell and taste begin to return." },
    { hours: 72, title: "3 days", desc: "Breathing feels easier as bronchial tubes relax." },
    { hours: 336, title: "2 weeks", desc: "Circulation improves and lung function increases." },
    { hours: 720, title: "1 month", desc: "Coughing and shortness of breath decrease noticeably." },
    { hours: 8760, title: "1 year", desc: "Risk of coronary heart disease is cut in half." },
  ],
  vaping: [
    { hours: 0.33, title: "20 minutes", desc: "Heart rate and blood pressure begin to settle back toward normal." },
    { hours: 12, title: "12 hours", desc: "Carbon monoxide falls and oxygen in your blood improves." },
    { hours: 48, title: "2 days", desc: "Nerve endings recover — taste and smell start to sharpen." },
    { hours: 72, title: "3 days", desc: "Nicotine has cleared your body; breathing feels a little easier." },
    { hours: 336, title: "2 weeks", desc: "Circulation and lung function improve; less coughing." },
    { hours: 720, title: "1 month", desc: "Airway irritation eases and energy steadies." },
    { hours: 8760, title: "1 year", desc: "Long-term heart and lung risks fall significantly." },
  ],
  chew: [
    { hours: 0.33, title: "20 minutes", desc: "Heart rate and blood pressure begin to settle back toward normal." },
    { hours: 12, title: "12 hours", desc: "Blood nicotine drops sharply; your body starts to reset." },
    { hours: 48, title: "2 days", desc: "Taste begins to return and mouth irritation starts to calm." },
    { hours: 72, title: "3 days", desc: "Nicotine has cleared your body; cravings begin to ease." },
    { hours: 336, title: "2 weeks", desc: "Gums and mouth tissue begin to heal." },
    { hours: 720, title: "1 month", desc: "Oral health improves and energy steadies." },
    { hours: 8760, title: "1 year", desc: "Long-term oral and heart risks fall significantly." },
  ],
};

// 72-hour keep-busy toolkit
export const TOOLKIT = [
  { id: "breathe", icon: "Wind", title: "Guided Breathing", desc: "A slow 4-second inhale, 6-second exhale to calm the nervous system.", tag: "Calm", type: "breathe" },
  { id: "urge", icon: "Timer", title: "Ride the Urge", desc: "Cravings crest and fall in about 10 minutes. Start the timer and outlast it.", tag: "Delay", type: "urge" },
  { id: "prayer", icon: "BookOpenText", title: "Prayer & Scripture", desc: "Pause and pray. Read a verse of strength and let it steady you.", tag: "Faith", type: "prayer" },
  { id: "walk", icon: "Footprints", title: "Move Your Body", desc: "A brisk 10-minute walk floods you with natural, clean energy.", tag: "Body", type: "info" },
  { id: "water", icon: "GlassWater", title: "Hydrate & Nourish", desc: "Drink a full glass of water. The HALT check: are you Hungry, Angry, Lonely, Tired?", tag: "Body", type: "info" },
  { id: "call", icon: "PhoneCall", title: "Reach Out", desc: "Text or call one trusted person. You were never meant to do this alone.", tag: "Connect", type: "info" },
  { id: "journal", icon: "PenLine", title: "Name It", desc: "Write down what you're feeling and the trigger. Naming it loosens its grip.", tag: "Mind", type: "info" },
  { id: "hands", icon: "Hand", title: "Busy Hands", desc: "Wash dishes, fold laundry, hold ice, or squeeze a stress ball for 5 minutes.", tag: "Distract", type: "info" },
];

export const CRISIS_RESOURCES = [
  { name: "988 Suicide & Crisis Lifeline", detail: "Call or text 988 — free, confidential, 24/7.", tel: "988", country: "US" },
  { name: "SAMHSA National Helpline", detail: "1-800-662-4357 — free, confidential treatment referral, 24/7.", tel: "18006624357", country: "US" },
  { name: "Emergency Services", detail: "Call 911 for chest pain, trouble breathing, severe confusion, or thoughts of self-harm.", tel: "911", country: "US" },
  { name: "Crisis Text Line", detail: "Text HOME to 741741 to connect with a crisis counselor.", tel: "741741", country: "US" },
];

export const GROUNDING_STEPS = [
  "Name 5 things you can see around you.",
  "Name 4 things you can physically feel.",
  "Name 3 things you can hear.",
  "Name 2 things you can smell.",
  "Name 1 thing you can taste.",
  "Take a slow breath. You are here. You are safe. God is with you.",
];

export const COMMON_TRIGGERS = [
  "Morning coffee",
  "After meals",
  "Driving",
  "Work stress",
  "Coffee or drinks with friends",
  "Boredom",
  "Phone / screen breaks",
  "Seeing others smoke",
  "Right after waking",
  "Before bed",
  "Anxiety",
  "Celebrations",
];

export const GETAWAY_TYPES = [
  { id: "roadtrip", label: "Road trip", icon: "Car", desc: "Open road, new scenery, hands on the wheel" },
  { id: "camping", label: "Camping", icon: "Tent", desc: "Nature, fresh air, campfires and stars" },
  { id: "staycation", label: "Staycation", icon: "Sofa", desc: "A restful reset close to home" },
  { id: "dayhikes", label: "Day hikes", icon: "Mountain", desc: "Trails, movement, big views" },
  { id: "none", label: "At home", icon: "Home", desc: "A keep-busy plan without traveling" },
];

export const ENERGY_LEVELS = [
  { id: "low", label: "Low / restorative", desc: "Gentle & easy — expect withdrawal fatigue" },
  { id: "medium", label: "Balanced", desc: "A mix of rest and light activity" },
  { id: "high", label: "Active", desc: "Keep moving to stay distracted" },
];

export const ACTIVITY_PREFS = [
  "Spa & wellness",
  "Hot springs",
  "Horseback riding",
  "Mild hikes",
  "Swimming",
  "Scenic drives",
  "Lakes & rivers",
  "Nature & parks",
  "Cozy cafés",
  "Yoga & stretching",
  "Fishing",
  "Beaches",
  "Photography",
  "Hammock & rest",
];
